import { ColDef, ColGroupDef, ValueGetterParams } from 'ag-grid-community'
import { useMemo } from 'react'
import { intl } from '../../../../i18n'
import { useRowNumber } from '../../../page-properties/bulksheet-properties/rowNumber'
import { useCode } from '../../../page-properties/bulksheet-properties/common'
import {
  useCreatedAt,
  useCreatedBy,
  useRevision,
  useUpdatedAt,
  useUpdatedBy,
} from '../../../page-properties/bulksheet-properties/changeLog'
import { ColumnType } from '../../../containers/commons/AgGrid'
import DateCell from '../../../containers/commons/AgGrid/components/cell/common/date'
import { EntitySearchCellRenderer } from '../../../containers/BulkSheetView/components/cellRenderer'
import {
  ClientSideSelectFilter,
  ClientSideTextFilter,
} from '../../../containers/BulkSheetView/components/filter'
import { UserBasic } from '../../../../lib/functions/user'
import {
  ProgramOverviewDto,
  ProgramStatus,
} from '../../../../infrastructure/api/programs'
import { CollaboratorsCellRenderer } from './CollaboratorsCellRenderer'
import { TransitionIconsCellRenderer } from './TransitionIconsCellRenderer'

type ProgramColumnDef = ColDef | ColGroupDef<ProgramOverviewDto>

export const useColumnDefs = (): ProgramColumnDef[] => {
  const rowNumberCol = useRowNumber()
  const codeCol = useCode('code')
  const revisionCol = useRevision('revision')
  const createdByCol = useCreatedBy('createdBy')
  const createdAtCol = useCreatedAt('createdAt')
  const updatedByCol = useUpdatedBy('updatedBy')
  const updatedAtCol = useUpdatedAt('updatedAt')

  return useMemo<ProgramColumnDef[]>(
    () => [
      {
        field: 'uuid',
        hide: true,
        suppressColumnsToolPanel: true,
      },
      { ...rowNumberCol, pinned: 'left' as const },
      {
        colId: 'transitionIcons',
        headerName: '',
        width: 60,
        pinned: 'left',
        cellRenderer: TransitionIconsCellRenderer,
      },
      {
        headerName: intl.formatMessage({
          id: 'programList.list.headerGroup.basic',
        }),
        children: [
          { ...codeCol, hide: false, pinned: 'left', width: 130 },
          {
            field: 'status',
            headerName: intl.formatMessage({
              id: 'programList.list.column.status',
            }),
            width: 130,
            pinned: 'left',
            filter: ClientSideSelectFilter,
            floatingFilter: true,
            filterParams: {
              optionKey: 'programStatuses',
              getValuesForOptionFilter: async (context: {
                programStatuses: readonly ProgramStatus[]
              }) => [...context.programStatuses],
              getValue: (v?: ProgramStatus | null) => v ?? '',
              getLabel: (v?: ProgramStatus | null) => v ?? '',
            },
          },
          {
            colId: 'division',
            headerName: intl.formatMessage({
              id: 'programList.list.column.division',
            }),
            width: 160,
            pinned: 'left',
            filter: ClientSideTextFilter,
            floatingFilter: true,
            valueGetter: (params: ValueGetterParams<ProgramOverviewDto>) =>
              params.data?.division?.displayName ?? '',
          },
          {
            field: 'displayName',
            headerName: intl.formatMessage({
              id: 'programList.list.column.displayName',
            }),
            width: 280,
            pinned: 'left',
            filter: ClientSideTextFilter,
            floatingFilter: true,
          },
        ],
      },
      {
        headerName: intl.formatMessage({
          id: 'programList.list.headerGroup.period',
        }),
        children: [
          {
            field: 'scheduledStartDate',
            headerName: intl.formatMessage({
              id: 'programList.list.column.scheduledStartDate',
            }),
            type: [ColumnType.date],
            width: 130,
            filter: DateCell.cellFilter,
            floatingFilter: true,
          },
          {
            field: 'scheduledEndDate',
            headerName: intl.formatMessage({
              id: 'programList.list.column.scheduledEndDate',
            }),
            type: [ColumnType.date],
            width: 130,
            filter: DateCell.cellFilter,
            floatingFilter: true,
          },
        ],
      },
      {
        field: 'programManager',
        headerName: intl.formatMessage({
          id: 'programList.list.column.programManager',
        }),
        width: 180,
        cellRenderer: EntitySearchCellRenderer,
        filter: ClientSideSelectFilter,
        floatingFilter: true,
        filterParams: {
          getValue: (v?: UserBasic) => v?.name ?? '',
          getLabel: (v?: UserBasic) => v?.name ?? '',
        },
      },
      {
        field: 'collaborators',
        headerName: intl.formatMessage({
          id: 'programList.list.column.collaborators',
        }),
        width: 180,
        cellRenderer: CollaboratorsCellRenderer,
        filter: ClientSideSelectFilter,
        floatingFilter: true,
        filterParams: {
          getValue: (v?: UserBasic) => v?.name ?? '',
          getLabel: (v?: UserBasic) => v?.name ?? '',
        },
      },
      {
        headerName: intl.formatMessage({ id: 'changeLog' }),
        children: [
          revisionCol,
          createdByCol,
          createdAtCol,
          updatedByCol,
          updatedAtCol,
        ],
      },
    ],
    [
      rowNumberCol,
      codeCol,
      revisionCol,
      createdByCol,
      createdAtCol,
      updatedByCol,
      updatedAtCol,
    ]
  )
}
